import { useState, useRef, useEffect, type ChangeEvent } from 'react';
import Modal from '../base/Modal';
import ImageUploadService from '../../services/imageUploadService';

interface IconSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectIcon: (icon: string) => void;
  selectedIcon?: string;
  gender: 'male' | 'female';
  memberName?: string;
}

const peopleIcons = {
  male: [
    '/Imagens/bebe 6.1.webp',
    '/Imagens/bebe 6.2.webp',
    '/Imagens/bebe 6.3.webp',
    '/Imagens/bebe 6.4.webp',
    '/Imagens/bebe 6.5.webp',
    '/Imagens/criança 71.webp',
    '/Imagens/criança 7.2.webp',
    '/Imagens/criança 7.3.webp',
    '/Imagens/criança 7.4.webp',
    '/Imagens/criança 7.5.webp',
    '/Imagens/adolescente 8.1.webp',
    '/Imagens/adolescente 8.2.webp',
    '/Imagens/adolescente 8.3.webp',
    '/Imagens/adolescente 8.4.webp',
    '/Imagens/adolescente 8.5.webp',
    '/Imagens/adulto 9.1.webp',
    '/Imagens/adulto 9.2.webp',
    '/Imagens/adulto 9.3.webp',
    '/Imagens/adulto 9.4.webp',
    '/Imagens/adulto 9.5.webp',
    '/Imagens/idoso 10.1.webp',
    '/Imagens/idoso 10.2.webp',
    '/Imagens/idoso 10.3.webp',
    '/Imagens/idoso 10.4.webp',
    '/Imagens/idoso 10.5.webp'
  ],
  female: [
    '/Imagens/bebe 1.1.webp',
    '/Imagens/bebe 1.2.webp',
    '/Imagens/bebe 1.3.webp',
    '/Imagens/bebe 1.4.webp',
    '/Imagens/bebe 1.5.webp',
    '/Imagens/criança 2.1.webp',
    '/Imagens/criança 2.2.webp',
    '/Imagens/criança 2.3.webp',
    '/Imagens/criança 2.4.webp',
    '/Imagens/criança 2.5.webp',
    '/Imagens/adolescente 3.1.webp',
    '/Imagens/adolescente 3.2.webp',
    '/Imagens/Adolescente 3.3.webp',
    '/Imagens/adolescente 3.4.webp',
    '/Imagens/adolescente 3.5.webp',
    '/Imagens/Adulto 4.1.webp',
    '/Imagens/Adulto 4.2.webp',
    '/Imagens/Adulto 4.3.webp',
    '/Imagens/Adulto 4.4.webp',
    '/Imagens/Adulto 4.5.webp',
    '/Imagens/idoso 5.1.webp',
    '/Imagens/idoso 5.2.webp',
    '/Imagens/idoso 5.3.webp',
    '/Imagens/idoso 5.4.webp',
    '/Imagens/idoso 5.5.webp'
  ]
};

export default function IconSelectorModal({
  isOpen,
  onClose,
  onSelectIcon,
  selectedIcon,
  gender,
  memberName
}: IconSelectorModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleIconSelect = (icon: string) => {
    onSelectIcon(icon);
    onClose();
    try { window.dispatchEvent(new CustomEvent('tutorial:avatar:selected')); } catch { /* ignore */ }
  };

  const availableIcons = peopleIcons[gender];
  const isImageIcon = (icon: string) => icon.startsWith('/Imagens/') || icon.startsWith('http') || icon.startsWith('data:');

  // Sinalizar ao tutorial que o modal abriu (para aguardar no step de avatar)
  useEffect(() => {
    if (isOpen) {
      try { window.dispatchEvent(new CustomEvent('tutorial:avatar:modal-open')); } catch { /* ignore */ }
    }
  }, [isOpen]);

  const handleCustomAvatarClick = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadError(null);
      setIsUploading(true);

      const uploadService = ImageUploadService.getInstance();
      const uploadedUrl = await uploadService.uploadMemberAvatar(file, memberName || 'member');

      onSelectIcon(uploadedUrl);
      onClose();
      try { window.dispatchEvent(new CustomEvent('tutorial:avatar:selected')); } catch { /* ignore */ }
    } catch (error: any) {
      console.error('❌ [IconSelectorModal] Erro ao enviar avatar:', error);
      setUploadError(error?.message || 'Não foi possível enviar a imagem. Tente novamente.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Escolher Ícone do Perfil"
      size="lg"
      overlayClassName="z-[70]"
    >
      <div className="space-y-6">
        <div data-tutorial-anchor="member-avatar-combined">
          {/* Grade de Ícones */}
          <div>
          <p className="text-sm font-medium text-gray-700 mb-3 font-sans">
            Escolha um ícone para seu Perfil
          </p>
          <div className="grid grid-cols-5 gap-4" data-tutorial-anchor="member-avatar-picker">
            {availableIcons.map((icon) => (
              <button
                key={icon}
                onClick={() => handleIconSelect(icon)}
                className={`w-16 h-16 rounded-xl flex items-center justify-center transition-all overflow-hidden ${
                  selectedIcon === icon
                    ? 'bg-purple-100 border-2 border-purple-300 text-purple-600'
                    : 'bg-gray-100 border-2 border-transparent hover:bg-gray-200 text-gray-600'
                }`}
              >
                {isImageIcon(icon) ? (
                  <img
                    src={icon}
                    alt="Avatar"
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <i className={`${icon} text-2xl`}></i>
                )}
              </button>
            ))}
          </div>
          </div>

          <div className="pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-600 mb-3 font-sans">
              Prefere usar uma foto real? Envie uma imagem do seu dispositivo.
            </p>
            <button
              type="button"
              onClick={handleCustomAvatarClick}
              disabled={isUploading}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-medium transition-colors font-sans ${
                isUploading
                  ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'border-purple-200 text-purple-600 hover:bg-purple-50'
              }`}
            >
              <i className="ri-upload-2-line text-lg"></i>
              {isUploading ? 'Enviando foto...' : 'Selecionar foto do dispositivo'}
            </button>
            {uploadError && (
              <p className="text-sm text-red-500 mt-2 font-sans">
                {uploadError}
              </p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
